// lib/screenEvents.js — LOS AVISOS DE PANTALLA, y nada más.
//
// POR QUÉ EXISTE (F6.8 S-2, pieza 1). El carril de estudio tenía que avisarle al
// cache de pantalla que acababa de escribir, y hay una regla de capa que no se
// negocia: los runtimes (`lib/brain/*`) NO importan `screenDataCache`. El patrón
// que el repo YA usa para eso es el evento de ventana — el cache se suscribe a
// eventos que otros emiten (`studybuddy:docs-changed`,
// `studybuddy:local-material-ready`, `studybuddy:pending-changed`) y nadie
// aguas arriba sabe que el cache existe.
//
// Este módulo es la HOJA de ese patrón: cero imports, cero estado, cero efectos.
// Solo el nombre del evento y el disparo con su guardia. Puede importarlo
// cualquiera —runtime, ensamblador de bodega o página— sin crear un ciclo ni
// arrastrar una capa detrás.
//
// LA GUARDIA. La emisión es FIRE-AND-FORGET en el sentido estricto: sin `window`
// (un test de runtime puro, un entorno sin DOM) no truena ni cambia nada; un
// `dispatchEvent` que falle se traga aquí. Emitir jamás puede romper una
// entrega: la entrega ya ocurrió y quedó guardada antes de llegar a esta línea.

/**
 * El aviso de que una SESIÓN DE ESTUDIO quedó COMMITEADA (entregada, cerrada o
 * abandonada). Lo emiten los dos runtimes locales y las páginas en su rama web,
 * SIEMPRE después del commit exitoso y JAMÁS dentro de la transacción.
 */
export const SESSION_COMMITTED_EVENT = 'studybuddy:session-committed';

// Las llaves de la fila del Study Log que un cierre PUEDE cambiar, y las únicas
// que viajan en el aviso. Lo que no cambia al cerrar (`started_at`,
// `document_ids`, `document_names`, `time_limit`) no se copia: un parche que
// re-escribe lo que ya estaba es un parche que puede equivocarse.
const CAMPOS_DEL_CIERRE = [
  'mode', 'status', 'completed', 'completed_at',
  'score', 'correct_count', 'total_questions', 'total_answered',
];

/**
 * El PARCHE: solo lo que la app sabe de CERTEZA por el resultado del propio
 * submit/cierre. Un campo ausente o `undefined` no viaja (parchar con
 * `undefined` borraría el valor bueno que la copia ya tenía).
 *
 * Devuelve `null` sin `id`: sin identidad no hay fila que parchar.
 */
export function sessionCommittedPatch(session) {
  if (!session || !session.id) return null;
  const patch = { id: session.id };
  for (const campo of CAMPOS_DEL_CIERRE) {
    if (session[campo] !== undefined) patch[campo] = session[campo];
  }
  return patch;
}

/**
 * Emite el aviso. `session` es la sesión ya commiteada (la que el driver
 * devuelve) o el objeto mínimo que la página arma con la respuesta del server.
 * Sin `id` no se emite nada.
 */
export function emitSessionCommitted(session) {
  const patch = sessionCommittedPatch(session);
  if (!patch) return;
  try {
    if (typeof window === 'undefined' || !window.dispatchEvent) return;
    window.dispatchEvent(new CustomEvent(SESSION_COMMITTED_EVENT, {
      detail: { session: patch },
    }));
  } catch (e) {
    // Fire-and-forget: avisar jamás rompe lo que ya quedó escrito.
  }
}

/**
 * F6.8 S-2 (pieza 3) — LA FOTO DE AYER SE CORRIGE SOLA.
 *
 * Una lectura que se sirvió de la FOTO (el motor rechazó) es la última foto
 * conocida, no la verdad de ahora: no trae las entregas locales que el servidor
 * aún no conoce ni el sello de los vencidos. Cuando el motor por fin responde
 * para ESA misma lectura, la verdad viaja por aquí: el cache la re-escribe y la
 * pantalla montada se corrige pintando UNA vez.
 *
 * Misma capa y mismo mecanismo que el aviso de arriba — el ensamblador de bodega
 * tampoco importa `screenDataCache`.
 */
export const SCREEN_CORRECTED_EVENT = 'studybuddy:screen-corrected';

export function emitScreenCorrected(key, data) {
  if (!key || data === undefined) return;
  try {
    if (typeof window === 'undefined' || !window.dispatchEvent) return;
    window.dispatchEvent(new CustomEvent(SCREEN_CORRECTED_EVENT, {
      detail: { key, data },
    }));
  } catch (e) {
    // Fire-and-forget, igual que el otro: corregir jamás puede tumbar nada.
  }
}
